import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import BuildkiteIcon from "src/components/ui/icons/buildkite";
import { getProductName } from "src/utils/brandTheme";

import BuildkiteIntegrationForm from "./BuildkiteIntegrationForm";

interface BuildkiteIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const BuildkiteInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Token Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Buildkite account.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Personal Settings</span>
            {" > "}
            <span className="text-amber-600">API Access Tokens</span>.
          </li>
          <li>
            Click on <span className="text-amber-600">New API Access Token</span>.
          </li>
          <li>
            Enter a description for your token (e.g., "{productName} Integration").
          </li>
          <li>
            Select the organization you want to connect.
          </li>
          <li>
            Select all <span className="text-amber-600">"read_"</span> scopes.
          </li>
          <li>
            Click <span className="text-amber-600">Create New API Access Token</span>.
          </li>
          <li>
            Copy the generated token and paste it into the API Token field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Organization Slug Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
        <li>
            Your organization slug can be found in your Buildkite URL, e.g.{" "}
            <span className="font-mono text-green-400">https://buildkite.com/ORGANIZATION_SLUG</span>.
          </li>
          <li>
            Copy your organization slug and paste it into the Organization Slug field below.
          </li>
        </ol>
      </div>
    </div>
  );
};

export default function BuildkiteIntegration({ isOpen, onClose, onRefetch, brandTheme }: BuildkiteIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <BuildkiteIcon className="h-6 w-6" />
            Connect Buildkite
          </DialogTitle>
          <DialogDescription>
            Connect your Buildkite account to give {productName} access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <BuildkiteInstructions productName={productName} />
            </div>
          </div>

          <BuildkiteIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}